import { INTERNAL_ROLES } from "./known-roles";

export interface InternalPrincipal {
  internal?: boolean | null;
  roles?: readonly string[] | null;
  email?: string | null;
}

const hasIntersection = (left: readonly string[], right: readonly string[]): boolean =>
  left.some((value) => right.includes(value));

/**
 * The manual `internal` boolean.
 * Origin: CRM guard, HasInternalRole, and analytics middleware.
 * Currently protects CRM proxy authorization, internal/external UI gates, and analytics read access.
 */
export const isInternalFlag = (u: InternalPrincipal): boolean => u.internal === true;

/**
 * Membership in the canonical internal role set.
 * Origin: API RBAC isInternalUser and analytics assertInternal.
 * Currently protects API internal-only RBAC paths and analytics read controllers.
 */
export const hasInternalRole = (u: InternalPrincipal): boolean => hasIntersection(u.roles ?? [], INTERNAL_ROLES);

/**
 * Agent role plus a QuoteWell email domain.
 * Origin: FE helper hasInternalRoleOrInternalAdmin.
 * Currently protects the select-markets.tsx site-local grant.
 */
export const isInternalAgentDomain = (u: InternalPrincipal): boolean =>
  (u.roles ?? []).includes("Agent") && !!u.email?.toLowerCase().endsWith("@quotewell.com");

/**
 * Universal internal-user predicate.
 * Option B keeps the agent+domain grant site-local while consolidating flag and role checks.
 */
export const isInternal = (u: InternalPrincipal): boolean => {
  // B-1441 A/B: to universalize the agent+domain grant (option A), add ` || isInternalAgentDomain(u)` here — one-line flip, pending Landon's call.
  return isInternalFlag(u) || hasInternalRole(u);
};

/**
 * Version stamp for the shared internal-access policy. Recorded on every logged access decision so a
 * verdict can be traced to the exact policy that produced it — bump when the composition of `isInternal`
 * changes (e.g. dropping a redundant term, or the option-A agent+domain flip).
 */
export const INTERNAL_POLICY_VERSION = "internal.v1";

/**
 * Names which term(s) of `isInternal` a principal satisfies, for observability.
 * Returns the granting term when internal, or "none" when not — this is what makes it possible to see,
 * in the access log, whether a user got in via the manual flag or an internal role (the leading
 * indicator that the flag and role sets have drifted apart). Note: reflects the coarse `isInternal`
 * (flag || role); `agentDomain` is site-local and not part of the universal predicate.
 */
export const explainInternal = (u: InternalPrincipal): "flag+role" | "flag" | "role" | "none" => {
  const flag = isInternalFlag(u);
  const role = hasInternalRole(u);
  if (flag && role) {
    return "flag+role";
  }
  if (flag) {
    return "flag";
  }
  if (role) {
    return "role";
  }
  return "none";
};
